import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { coursesAPI } from '../../services/api';
import CourseCard from '../CourseCard';
import Button from '../UIKit/Button';
import Card from '../UIKit/Card';

const SimilarCoursesByProfessions = ({ courseId, limit = 6 }) => {
  const { t } = useTranslation(['courses', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);

  const loadSimilarCourses = useCallback(async () => {
    if (!courseId) return;
    
    try {
      setLoading(true);
      setError(null);
      const res = await coursesAPI.getSimilarByProfessions(courseId, { limit });
      setCourses(Array.isArray(res.data?.courses) ? res.data.courses : []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [courseId, limit]);

  useEffect(() => {
    let isMounted = true;
    
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await coursesAPI.getSimilarByProfessions(courseId, { limit });
        if (!isMounted) return;
        setCourses(Array.isArray(res.data?.courses) ? res.data.courses : []);
      } catch (e) {
        if (!isMounted) return;
        setError(e?.response?.data?.message || e.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    load();
    return () => { isMounted = false; };
  }, [courseId, limit]);

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <Card sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h3">
            {t('courses:courseDetail.similarByProfessions')}
          </Typography>
        </Box>
        <Typography variant="body2" color="error" mb={2}>
          {t('common:errors.loadError')}
        </Typography>
        <Box display="flex" justifyContent="center">
          <Button 
            variant="outlined" 
            onClick={loadSimilarCourses}
            disabled={loading}
          >
            {t('common:actions.retry')}
          </Button>
        </Box>
      </Card>
    );
  }

  if (!courses.length) return null;

  return (
    <Card sx={{ mb: 2 }}>
      <Typography variant="h6" component="h3" gutterBottom>
        {t('courses:courseDetail.similarByProfessions')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {courses.map((c) => (
          <CourseCard course={c} key={c._id} sx="w-full" />
        ))}
      </Box>
    </Card>
  );
};

export default SimilarCoursesByProfessions;


